import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { ShoppingBag, Menu, X, Flame } from "lucide-react";
import { useCart } from "../contexts/CartContext";

/**
 * Cinematic, magnetic top nav for the Grill Bros premium experience.
 * - Transparent at top, blur on scroll
 * - Magnetic hover links
 * - Animated logo
 */
export const CinematicNav = ({ onOpenCart }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { count } = useCart();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollY } = useScroll();

    useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 30));

    const links = [
        { to: "/", label: "Home" },
        { to: "/menu", label: "Menu" },
        { to: "/marketplace", label: "Explore", small: true },
    ];

    return (
        <>
            <motion.nav
                data-testid="cinematic-nav"
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.2, 0.9, 0.2, 1] }}
                className={`fixed top-0 left-0 right-0 z-[600] transition-all duration-500 ${
                    scrolled ? "py-3" : "py-5"
                }`}
            >
                <div
                    className={`mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between rounded-full transition-all duration-500 ${
                        scrolled
                            ? "bg-black/55 backdrop-blur-xl border border-white/8 py-2.5 mx-3 sm:mx-6 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                            : "py-2"
                    }`}
                >
                    {/* Logo */}
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
                        <span className="relative h-9 w-9 grid place-items-center rounded-xl ember-gradient overflow-hidden">
                            <Flame size={18} strokeWidth={2.5} className="text-black relative z-10" />
                            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
                        </span>
                        <span className="font-display text-2xl tracking-[0.18em] text-white group-hover:ember-text transition">
                            GRILL BROS
                        </span>
                    </Link>

                    {/* Desktop links */}
                    <div className="hidden md:flex items-center gap-1">
                        {links.map((l) => {
                            const active = location.pathname === l.to;
                            return (
                                <MagneticLink key={l.to} to={l.to} active={active} small={l.small}>
                                    {l.label}
                                </MagneticLink>
                            );
                        })}
                    </div>

                    {/* CTA cluster */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onOpenCart}
                            data-testid="nav-cart"
                            className="relative h-10 px-4 hidden sm:inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition group"
                        >
                            <ShoppingBag size={15} className="text-white/80 group-hover:text-[var(--burger-orange-2)]" />
                            <span className="font-display text-sm tracking-widest text-white/85">CART</span>
                            {count > 0 && (
                                <span className="ml-1 h-5 min-w-[20px] px-1.5 grid place-items-center rounded-full ember-gradient text-black text-[10px] font-bold">
                                    {count}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => navigate("/menu")}
                            data-testid="nav-order"
                            className="magnet-cta primary hidden sm:inline-flex h-10 px-5 text-xs"
                        >
                            Order Now
                        </button>
                        <button
                            onClick={() => setOpen(true)}
                            className="md:hidden h-10 w-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                            data-testid="nav-mobile-toggle"
                        >
                            <Menu size={18} />
                        </button>
                    </div>
                </div>
            </motion.nav>

            {/* Mobile drawer */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-drawer"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[700] bg-black/95 backdrop-blur-2xl md:hidden"
                    >
                        <motion.div
                            initial={{ y: -40 }} animate={{ y: 0 }} exit={{ y: -40 }}
                            transition={{ duration: 0.4, ease: [0.2,0.9,0.2,1] }}
                            className="h-full flex flex-col"
                        >
                            <div className="flex items-center justify-between px-5 py-5">
                                <span className="font-display text-2xl tracking-[0.18em]">GRILL BROS</span>
                                <button onClick={() => setOpen(false)} className="h-10 w-10 grid place-items-center rounded-full bg-white/10">
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="flex-1 flex flex-col items-start gap-2 px-8 mt-8">
                                {links.map((l, i) => (
                                    <motion.div
                                        key={l.to}
                                        initial={{ x: -40, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: 0.15 + i * 0.08, duration: 0.5, ease: [0.2,0.9,0.2,1] }}
                                    >
                                        <Link
                                            to={l.to}
                                            onClick={() => setOpen(false)}
                                            className="font-display text-5xl tracking-tight text-white hover:ember-text transition"
                                        >
                                            {l.label}
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>
                            <div className="px-8 pb-10 space-y-3">
                                <button
                                    onClick={() => { setOpen(false); onOpenCart?.(); }}
                                    className="w-full magnet-cta ghost h-14"
                                    data-testid="nav-mobile-cart"
                                >
                                    Cart {count > 0 && <span className="ml-1 px-2 rounded-full ember-gradient text-black text-xs">{count}</span>}
                                </button>
                                <button
                                    onClick={() => { setOpen(false); navigate("/menu"); }}
                                    className="w-full magnet-cta primary h-14"
                                    data-testid="nav-mobile-order"
                                >
                                    Order Now
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

const MagneticLink = ({ to, active, children, small }) => {
    const navigate = useNavigate();
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: (e.clientX - r.left - r.width / 2) * 0.25, y: (e.clientY - r.top - r.height / 2) * 0.25 });
    };
    return (
        <motion.button
            onClick={() => navigate(to)}
            onMouseMove={onMove}
            onMouseLeave={() => setPos({ x: 0, y: 0 })}
            animate={{ x: pos.x, y: pos.y }}
            transition={{ type: "spring", stiffness: 250, damping: 18 }}
            data-testid={`nav-link-${to.replace("/", "") || "home"}`}
            className={`relative px-4 py-2 rounded-full transition ${
                active ? "text-[var(--burger-orange-2)]" : "text-white/75 hover:text-white"
            } ${small ? "text-xs" : "text-sm"} font-display tracking-[0.2em] uppercase`}
        >
            {children}
            {active && (
                <motion.span
                    layoutId="nav-pill"
                    className="absolute inset-0 -z-10 rounded-full bg-[var(--burger-orange)]/15 border border-[var(--burger-orange)]/30"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
            )}
        </motion.button>
    );
};
